import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { generatePlatforms } from '@/features/settings/components/connectors/platforms';

/**
 * Enterprise-grade hook for Settings Page translations with type safety and error handling
 * Provides centralized access to all Settings-related translations with memoization
 */
export function useSettingspageTranslations() {
  // Use the default translation namespace since settingspage.json is merged into translation
  const { t } = useTranslation('translation');

  // Helper functions for common translation patterns with type safety
  const getSettingsText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.${key}`, params || {}));
  };

  const getValidationText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.validation.${key}`, params || {}));
  };

  const getPlaceholdersText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.placeholders.${key}`, params || {}));
  };

  const getTabsText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.tabs.${key}`, params || {}));
  };

  const getProfileText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.profile.${key}`, params || {}));
  };

  const getSecurityText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.security.${key}`, params || {}));
  };

  const getPreferencesText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.preferences.${key}`, params || {}));
  };

  const getOrganizationText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.organization.${key}`, params || {}));
  };

  const getApiText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.api.${key}`, params || {}));
  };

  const getAttributesText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.attributes.${key}`, params || {}));
  };

  const getAttributeGroupsText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.attributeGroups.${key}`, params || {}));
  };

  const getFamiliesText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.families.${key}`, params || {}));
  };

  const getErrorsText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.errors.${key}`, params || {}));
  };

  const getToastText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.toast.${key}`, params || {}));
  };

  const getAddLocaleModalText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.addLocaleModal.${key}`, params || {}));
  };

  const getAttributeValueRowText = (key: string, params?: Record<string, any>): string => {
    return String(t(`settingspage.attributeValueRow.${key}`, params || {}));
  };

  // Safe translation wrapper that always returns string
  const tSafe = (key: string, params?: Record<string, any>): string => {
    return String(t(key, params || {}));
  };

  // Memoized complex helpers to prevent re-renders
  const getMainPageText = useMemo(
    () => ({
      pageTitle: getSettingsText('pageTitle'),
      pageDescription: getSettingsText('pageDescription'),
    }),
    [t]
  );

  const getValidationLabels = useMemo(
    () => ({
      nameRequired: getValidationText('nameRequired'),
      firstNameRequired: getValidationText('firstNameRequired'),
      lastNameRequired: getValidationText('lastNameRequired'),
      invalidEmail: getValidationText('invalidEmail'),
      currentPasswordRequired: getValidationText('currentPasswordRequired'),
      newPasswordMinLength: getValidationText('newPasswordMinLength'),
      confirmPasswordRequired: getValidationText('confirmPasswordRequired'),
      passwordsDontMatch: getValidationText('passwordsDontMatch'),
      organizationNameRequired: getValidationText('organizationNameRequired'),
      defaultLocaleRequired: getValidationText('defaultLocaleRequired'),
      numberFormatRequired: getValidationText('numberFormatRequired'),
      dateFormatRequired: getValidationText('dateFormatRequired'),
      apiKeyNameRequired: getValidationText('apiKeyNameRequired'),
      apiKeyNameMaxLength: getValidationText('apiKeyNameMaxLength'),
      apiKeyPermissionsRequired: getValidationText('apiKeyPermissionsRequired'),
    }),
    [t]
  );

  const getPlaceholderLabels = useMemo(
    () => ({
      yourName: getPlaceholdersText('yourName'),
      yourFirstName: getPlaceholdersText('yourFirstName'),
      yourLastName: getPlaceholdersText('yourLastName'),
      yourEmail: getPlaceholdersText('yourEmail'),
      enterCurrentPassword: getPlaceholdersText('enterCurrentPassword'),
      enterNewPassword: getPlaceholdersText('enterNewPassword'),
      confirmNewPassword: getPlaceholdersText('confirmNewPassword'),
      selectDefaultLocale: getPlaceholdersText('selectDefaultLocale'),
      selectDefaultCurrency: getPlaceholdersText('selectDefaultCurrency'),
      selectNumberFormat: getPlaceholdersText('selectNumberFormat'),
      selectDateFormat: getPlaceholdersText('selectDateFormat'),
    }),
    [t]
  );

  const getTabLabels = useMemo(
    () => ({
      profile: getTabsText('profile'),
      security: getTabsText('security'),
      preferences: getTabsText('preferences'),
      organization: getTabsText('organization'),
      api: getTabsText('api'),
      attributes: getTabsText('attributes'),
      attributeGroups: getTabsText('attributeGroups'),
      'attribute-groups': getTabsText('attribute-groups'),
      families: getTabsText('families'),
    }),
    [t]
  );

  const getProfileLabels = useMemo(
    () => ({
      title: getProfileText('title'),
      description: getProfileText('description'),
      nameLabel: getProfileText('nameLabel'),
      firstNameLabel: getProfileText('firstNameLabel'),
      lastNameLabel: getProfileText('lastNameLabel'),
      emailLabel: getProfileText('emailLabel'),
      editButton: getProfileText('editButton'),
      saveButton: getProfileText('saveButton'),
      cancelButton: getProfileText('cancelButton'),
      updateButton: getProfileText('updateButton'),
      savingLabel: getProfileText('savingLabel'),
      successMessage: getProfileText('successMessage'),
    }),
    [t]
  );

  const getSecurityLabels = useMemo(
    () => ({
      title: getSecurityText('title'),
      description: getSecurityText('description'),
      currentPasswordLabel: getSecurityText('currentPasswordLabel'),
      newPasswordLabel: getSecurityText('newPasswordLabel'),
      confirmPasswordLabel: getSecurityText('confirmPasswordLabel'),
      updateButton: getSecurityText('updateButton'),
      savingLabel: getSecurityText('savingLabel'),
      successMessage: getSecurityText('successMessage'),
    }),
    [t]
  );

  const getPreferencesLabels = useMemo(
    () => ({
      title: getPreferencesText('title'),
      description: getPreferencesText('description'),
      themeTitle: getPreferencesText('themeTitle'),
      themeDescription: getPreferencesText('themeDescription'),
      lightModeLabel: getPreferencesText('lightModeLabel'),
      darkModeLabel: getPreferencesText('darkModeLabel'),
      systemModeLabel: getPreferencesText('systemModeLabel'),
      systemModeDescription: getPreferencesText('systemModeDescription'),
      themeDescription2: getPreferencesText('themeDescription2'),
      updateButton: getPreferencesText('updateButton'),
      savingLabel: getPreferencesText('savingLabel'),
      successMessage: getPreferencesText('successMessage'),
      themes: {
        light: getPreferencesText('themes.light'),
        dark: getPreferencesText('themes.dark'),
        system: getPreferencesText('themes.system'),
      },
    }),
    [t]
  );

  const getOrganizationLabels = useMemo(
    () => ({
      title: getOrganizationText('title'),
      description: getOrganizationText('description'),
      nameLabel: getOrganizationText('nameLabel'),
      defaultLocaleLabel: getOrganizationText('defaultLocaleLabel'),
      defaultChannelLabel: getOrganizationText('defaultChannelLabel'),
      defaultCurrencyLabel: getOrganizationText('defaultCurrencyLabel'),
      defaultPriceTypeLabel: getOrganizationText('defaultPriceTypeLabel'),
      numberFormatLabel: getOrganizationText('numberFormatLabel'),
      dateFormatLabel: getOrganizationText('dateFormatLabel'),
      addNewButton: getOrganizationText('addNewButton'),
      manageButton: getOrganizationText('manageButton'),
      localeDescription: getOrganizationText('localeDescription'),
      channelDescription: getOrganizationText('channelDescription'),
      currencyDescription: getOrganizationText('currencyDescription'),
      priceTypeDescription: getOrganizationText('priceTypeDescription'),
      numberFormatDescription: getOrganizationText('numberFormatDescription'),
      dateFormatDescription: getOrganizationText('dateFormatDescription'),
      updateButton: getOrganizationText('updateButton'),
      savingLabel: getOrganizationText('savingLabel'),
      successMessage: getOrganizationText('successMessage'),
      loadingMessage: getOrganizationText('loadingMessage'),
      errorMessage: getOrganizationText('errorMessage'),
      options: {
        none: getOrganizationText('options.none'),
        numberFormats: {
          dot: getOrganizationText('options.numberFormats.dot'),
          comma: getOrganizationText('options.numberFormats.comma'),
        },
        dateFormats: {
          MDY: getOrganizationText('options.dateFormats.MDY'),
          DMY: getOrganizationText('options.dateFormats.DMY'),
          YMD: getOrganizationText('options.dateFormats.YMD'),
        },
      },
      manageLocales: {
        title: getOrganizationText('manageLocales.title'),
        description: getOrganizationText('manageLocales.description'),
        addNewTitle: getOrganizationText('manageLocales.addNewTitle'),
        addLocaleButton: getOrganizationText('manageLocales.addLocaleButton'),
        cancelButton: getOrganizationText('manageLocales.cancelButton'),
        organizationLocalesTitle: getOrganizationText('manageLocales.organizationLocalesTitle'),
        organizationLocalesDescription: getOrganizationText(
          'manageLocales.organizationLocalesDescription'
        ),
        loadingMessage: getOrganizationText('manageLocales.loadingMessage'),
        noLocalesMessage: getOrganizationText('manageLocales.noLocalesMessage'),
        codeLabel: getOrganizationText('manageLocales.codeLabel'),
        codePlaceholder: getOrganizationText('manageLocales.codePlaceholder'),
        labelLabel: getOrganizationText('manageLocales.labelLabel'),
        labelPlaceholder: getOrganizationText('manageLocales.labelPlaceholder'),
        createButton: getOrganizationText('manageLocales.createButton'),
        closeButton: getOrganizationText('manageLocales.closeButton'),
        codeHeader: getOrganizationText('manageLocales.codeHeader'),
        labelHeader: getOrganizationText('manageLocales.labelHeader'),
        statusHeader: getOrganizationText('manageLocales.statusHeader'),
        actionsHeader: getOrganizationText('manageLocales.actionsHeader'),
        activeStatus: getOrganizationText('manageLocales.activeStatus'),
        inactiveStatus: getOrganizationText('manageLocales.inactiveStatus'),
        deleteConfirm: getOrganizationText('manageLocales.deleteConfirm'),
        createSuccess: getOrganizationText('manageLocales.createSuccess'),
        deleteSuccess: getOrganizationText('manageLocales.deleteSuccess'),
        createError: getOrganizationText('manageLocales.createError'),
        deleteError: getOrganizationText('manageLocales.deleteError'),
        validation: {
          codeMinLength: getOrganizationText('manageLocales.validation.codeMinLength'),
          codeMaxLength: getOrganizationText('manageLocales.validation.codeMaxLength'),
          labelRequired: getOrganizationText('manageLocales.validation.labelRequired'),
        },
      },
      manageChannels: {
        title: getOrganizationText('manageChannels.title'),
        description: getOrganizationText('manageChannels.description'),
        addNewTitle: getOrganizationText('manageChannels.addNewTitle'),
        addChannelButton: getOrganizationText('manageChannels.addChannelButton'),
        cancelButton: getOrganizationText('manageChannels.cancelButton'),
        organizationChannelsTitle: getOrganizationText('manageChannels.organizationChannelsTitle'),
        organizationChannelsDescription: getOrganizationText(
          'manageChannels.organizationChannelsDescription'
        ),
        loadingMessage: getOrganizationText('manageChannels.loadingMessage'),
        noChannelsMessage: getOrganizationText('manageChannels.noChannelsMessage'),
        nameLabel: getOrganizationText('manageChannels.nameLabel'),
        namePlaceholder: getOrganizationText('manageChannels.namePlaceholder'),
        codeLabel: getOrganizationText('manageChannels.codeLabel'),
        codePlaceholder: getOrganizationText('manageChannels.codePlaceholder'),
        createButton: getOrganizationText('manageChannels.createButton'),
        closeButton: getOrganizationText('manageChannels.closeButton'),
        deleteConfirm: getOrganizationText('manageChannels.deleteConfirm'),
      },
      manageCurrencies: {
        title: getOrganizationText('manageCurrencies.title'),
        description: getOrganizationText('manageCurrencies.description'),
        addNewTitle: getOrganizationText('manageCurrencies.addNewTitle'),
        addCurrencyButton: getOrganizationText('manageCurrencies.addCurrencyButton'),
        cancelButton: getOrganizationText('manageCurrencies.cancelButton'),
        organizationCurrenciesTitle: getOrganizationText(
          'manageCurrencies.organizationCurrenciesTitle'
        ),
        organizationCurrenciesDescription: getOrganizationText(
          'manageCurrencies.organizationCurrenciesDescription'
        ),
        loadingMessage: getOrganizationText('manageCurrencies.loadingMessage'),
        noCurrenciesMessage: getOrganizationText('manageCurrencies.noCurrenciesMessage'),
        isoCodeLabel: getOrganizationText('manageCurrencies.isoCodeLabel'),
        isoCodePlaceholder: getOrganizationText('manageCurrencies.isoCodePlaceholder'),
        nameLabel: getOrganizationText('manageCurrencies.nameLabel'),
        namePlaceholder: getOrganizationText('manageCurrencies.namePlaceholder'),
        symbolLabel: getOrganizationText('manageCurrencies.symbolLabel'),
        symbolPlaceholder: getOrganizationText('manageCurrencies.symbolPlaceholder'),
        decimalsLabel: getOrganizationText('manageCurrencies.decimalsLabel'),
        createButton: getOrganizationText('manageCurrencies.createButton'),
        closeButton: getOrganizationText('manageCurrencies.closeButton'),
        createSuccess: getOrganizationText('manageCurrencies.createSuccess'),
        deleteSuccess: getOrganizationText('manageCurrencies.deleteSuccess'),
        createError: getOrganizationText('manageCurrencies.createError'),
        deleteError: getOrganizationText('manageCurrencies.deleteError'),
        deleteConfirm: getOrganizationText('manageCurrencies.deleteConfirm'),
      },
      managePriceTypes: {
        title: getOrganizationText('managePriceTypes.title'),
        description: getOrganizationText('managePriceTypes.description'),
        addNewTitle: getOrganizationText('managePriceTypes.addNewTitle'),
        addPriceTypeButton: getOrganizationText('managePriceTypes.addPriceTypeButton'),
        cancelButton: getOrganizationText('managePriceTypes.cancelButton'),
        organizationPriceTypesTitle: getOrganizationText(
          'managePriceTypes.organizationPriceTypesTitle'
        ),
        organizationPriceTypesDescription: getOrganizationText(
          'managePriceTypes.organizationPriceTypesDescription'
        ),
        loadingMessage: getOrganizationText('managePriceTypes.loadingMessage'),
        noPriceTypesMessage: getOrganizationText('managePriceTypes.noPriceTypesMessage'),
        labelLabel: getOrganizationText('managePriceTypes.labelLabel'),
        labelPlaceholder: getOrganizationText('managePriceTypes.labelPlaceholder'),
        codeLabel: getOrganizationText('managePriceTypes.codeLabel'),
        codePlaceholder: getOrganizationText('managePriceTypes.codePlaceholder'),
        createButton: getOrganizationText('managePriceTypes.createButton'),
        closeButton: getOrganizationText('managePriceTypes.closeButton'),
      },
      tabs: {
        settings: getOrganizationText('tabs.settings'),
        subscription: getOrganizationText('tabs.subscription'),
      },
      subscription: {
        title: getOrganizationText('subscription.title'),
        description: getOrganizationText('subscription.description'),
        currentPlan: getOrganizationText('subscription.currentPlan'),
        planUsage: getOrganizationText('subscription.planUsage'),
        billingInfo: getOrganizationText('subscription.billingInfo'),
        changePlan: getOrganizationText('subscription.changePlan'),
        viewInvoices: getOrganizationText('subscription.viewInvoices'),
        cancelSubscription: getOrganizationText('subscription.cancelSubscription'),
        upgradeNow: getOrganizationText('subscription.upgradeNow'),
        downgradePlan: getOrganizationText('subscription.downgradePlan'),
        contactSupport: getOrganizationText('subscription.contactSupport'),
        loading: getOrganizationText('subscription.loading'),
        error: getOrganizationText('subscription.error'),
        comingSoon: getOrganizationText('subscription.comingSoon'),
        placeholder: getOrganizationText('subscription.placeholder'),
        status: {
          trialActive: getOrganizationText('subscription.status.trialActive'),
          active: getOrganizationText('subscription.status.active'),
          inactive: getOrganizationText('subscription.status.inactive'),
          noSubscription: getOrganizationText('subscription.status.noSubscription'),
          unknown: getOrganizationText('subscription.status.unknown'),
        },
        plans: {
          basic: getOrganizationText('subscription.plans.basic'),
          business: getOrganizationText('subscription.plans.business'),
          enterprise: getOrganizationText('subscription.plans.enterprise'),
          unknown: getOrganizationText('subscription.plans.unknown'),
        },
        billing: {
          annual: getOrganizationText('subscription.billing.annual'),
          monthly: getOrganizationText('subscription.billing.monthly'),
          manageBilling: getOrganizationText('subscription.billing.manageBilling'),
          legacyBilling: getOrganizationText('subscription.billing.legacyBilling'),
          noActiveBilling: getOrganizationText('subscription.billing.noActiveBilling'),
        },
        actions: {
          subscribe: getOrganizationText('subscription.actions.subscribe'),
          upgradePlan: getOrganizationText('subscription.actions.upgradePlan'),
          downgrade: getOrganizationText('subscription.actions.downgrade'),
          opening: getOrganizationText('subscription.actions.opening'),
          syncing: getOrganizationText('subscription.actions.syncing'),
          viewInvoices: getOrganizationText('subscription.actions.viewInvoices'),
          noInvoicesAvailable: getOrganizationText('subscription.actions.noInvoicesAvailable'),
        },
        messages: {
          trialExpiresIn: getOrganizationText('subscription.messages.trialExpiresIn'),
          billedMonthly: getOrganizationText('subscription.messages.billedMonthly'),
          billedAnnually: getOrganizationText('subscription.messages.billedAnnually'),
          noActiveSubscription: getOrganizationText('subscription.messages.noActiveSubscription'),
          trialStarted: getOrganizationText('subscription.messages.trialStarted'),
          trialExpires: getOrganizationText('subscription.messages.trialExpires'),
          upgradeToKernLogic: getOrganizationText('subscription.messages.upgradeToKernLogic'),
          legacyBillingDescription: getOrganizationText(
            'subscription.messages.legacyBillingDescription'
          ),
          subscribeToAccessPortal: getOrganizationText(
            'subscription.messages.subscribeToAccessPortal'
          ),
          downloadBillingHistory: getOrganizationText(
            'subscription.messages.downloadBillingHistory'
          ),
          invoiceHistoryNotAvailable: getOrganizationText(
            'subscription.messages.invoiceHistoryNotAvailable'
          ),
          noActiveSubscriptionFound: getOrganizationText(
            'subscription.messages.noActiveSubscriptionFound'
          ),
          openingBillingPortal: getOrganizationText('subscription.messages.openingBillingPortal'),
          unableToAccessBillingPortal: getOrganizationText(
            'subscription.messages.unableToAccessBillingPortal'
          ),
          yourTrialEndsOn: getOrganizationText('subscription.messages.yourTrialEndsOn'),
          startedOn: getOrganizationText('subscription.messages.startedOn'),
          expiresOn: getOrganizationText('subscription.messages.expiresOn'),
        },
        modal: {
          title: getOrganizationText('subscription.modal.title'),
          description: getOrganizationText('subscription.modal.description'),
          mostPopular: getOrganizationText('subscription.modal.mostPopular'),
          cancel: getOrganizationText('subscription.modal.cancel'),
          upgradeSummary: getOrganizationText('subscription.modal.upgradeSummary'),
          upgradingFromTo: getOrganizationText('subscription.modal.upgradingFromTo'),
          annualDiscount: getOrganizationText('subscription.modal.annualDiscount'),
          annualDiscountShort: getOrganizationText('subscription.modal.annualDiscountShort'),
          upgradeInfo: getOrganizationText('subscription.modal.upgradeInfo'),
          planFeatures: {
            basic: {
              skus: getOrganizationText('subscription.modal.planFeatures.basic.skus'),
              storage: getOrganizationText('subscription.modal.planFeatures.basic.storage'),
              users: getOrganizationText('subscription.modal.planFeatures.basic.users'),
              support: getOrganizationText('subscription.modal.planFeatures.basic.support'),
              connectors: getOrganizationText('subscription.modal.planFeatures.basic.connectors'),
            },
            business: {
              skus: getOrganizationText('subscription.modal.planFeatures.business.skus'),
              storage: getOrganizationText('subscription.modal.planFeatures.business.storage'),
              users: getOrganizationText('subscription.modal.planFeatures.business.users'),
              support: getOrganizationText('subscription.modal.planFeatures.business.support'),
              connectors: getOrganizationText(
                'subscription.modal.planFeatures.business.connectors'
              ),
            },
            enterprise: {
              skus: getOrganizationText('subscription.modal.planFeatures.enterprise.skus'),
              storage: getOrganizationText('subscription.modal.planFeatures.enterprise.storage'),
              users: getOrganizationText('subscription.modal.planFeatures.enterprise.users'),
              support: getOrganizationText('subscription.modal.planFeatures.enterprise.support'),
              connectors: getOrganizationText(
                'subscription.modal.planFeatures.enterprise.connectors'
              ),
            },
          },
        },
        cardTitles: {
          currentSubscription: getOrganizationText('subscription.cardTitles.currentSubscription'),
          subscriptionManagement: getOrganizationText(
            'subscription.cardTitles.subscriptionManagement'
          ),
        },
        fieldLabels: {
          plan: getOrganizationText('subscription.fieldLabels.plan'),
          billingCycle: getOrganizationText('subscription.fieldLabels.billingCycle'),
          started: getOrganizationText('subscription.fieldLabels.started'),
          trialStarted: getOrganizationText('subscription.fieldLabels.trialStarted'),
          trialExpires: getOrganizationText('subscription.fieldLabels.trialExpires'),
        },
      },
    }),
    [t, getOrganizationText]
  );

  const getApiLabels = useMemo(
    () => ({
      title: getApiText('title'),
      description: getApiText('description'),
      adminAccessRequired: getApiText('adminAccessRequired'),
      adminAccessMessage: getApiText('adminAccessMessage'),
      loadingPermissions: getApiText('loadingPermissions'),
      tabs: {
        platforms: getApiText('tabs.platforms'),
        products: getApiText('tabs.products'),
        activity: getApiText('tabs.activity'),
        analytics: getApiText('tabs.analytics'),
      },
      platforms: {
        title: getApiText('platforms.title'),
        description: getApiText('platforms.description'),
        refresh: getApiText('platforms.refresh'),
        addPlatform: getApiText('platforms.addPlatform'),
        loadingConfigurations: getApiText('platforms.loadingConfigurations'),
        noPlatforms: {
          title: getApiText('platforms.noPlatforms.title'),
          description: getApiText('platforms.noPlatforms.description'),
          addFirst: getApiText('platforms.noPlatforms.addFirst'),
        },
        badges: {
          enabled: getApiText('platforms.badges.enabled'),
          disabled: getApiText('platforms.badges.disabled'),
          autoSync: getApiText('platforms.badges.autoSync'),
        },
        lastSync: getApiText('platforms.lastSync'),
        nextSync: getApiText('platforms.nextSync'),
        created: getApiText('platforms.created'),
        actions: {
          test: getApiText('platforms.actions.test'),
          sync: getApiText('platforms.actions.sync'),
          syncing: getApiText('platforms.actions.syncing'),
          syncAll: getApiText('platforms.actions.syncAll'),
          syncingAll: getApiText('platforms.actions.syncingAll'),
          editConfiguration: getApiText('platforms.actions.editConfiguration'),
          enable: getApiText('platforms.actions.enable'),
          disable: getApiText('platforms.actions.disable'),
          enableAutoSync: getApiText('platforms.actions.enableAutoSync'),
          disableAutoSync: getApiText('platforms.actions.disableAutoSync'),
          delete: getApiText('platforms.actions.delete'),
          addPlatform: getApiText('platforms.actions.addPlatform'),
          addDescription: getApiText('platforms.actions.addDescription'),
          editDescription: getApiText('platforms.actions.editDescription'),
        },
        list: generatePlatforms({
          platforms: {
            shopify: {
              label: getApiText('platforms.shopify.label'),
              description: getApiText('platforms.shopify.description'),
              fields: {
                shopDomain: {
                  label: getApiText('platforms.shopify.fields.shopDomain.label'),
                  placeholder: getApiText('platforms.shopify.fields.shopDomain.placeholder'),
                  description: getApiText('platforms.shopify.fields.shopDomain.description'),
                },
                accessToken: {
                  label: getApiText('platforms.shopify.fields.accessToken.label'),
                  placeholder: getApiText('platforms.shopify.fields.accessToken.placeholder'),
                  description: getApiText('platforms.shopify.fields.accessToken.description'),
                },
                apiVersion: {
                  label: getApiText('platforms.shopify.fields.apiVersion.label'),
                  description: getApiText('platforms.shopify.fields.apiVersion.description'),
                  options: {
                    '2025-04': getApiText('platforms.shopify.fields.apiVersion.options.2025-04'),
                    '2025-01': getApiText('platforms.shopify.fields.apiVersion.options.2025-01'),
                    '2024-10': getApiText('platforms.shopify.fields.apiVersion.options.2024-10'),
                    '2024-01': getApiText('platforms.shopify.fields.apiVersion.options.2024-01'),
                  },
                },
              },
            },
            magento: {
              label: getApiText('platforms.magento.label'),
              description: getApiText('platforms.magento.description'),
              fields: {
                baseUrl: {
                  label: getApiText('platforms.magento.fields.baseUrl.label'),
                  placeholder: getApiText('platforms.magento.fields.baseUrl.placeholder'),
                  description: getApiText('platforms.magento.fields.baseUrl.description'),
                },
                consumerKey: {
                  label: getApiText('platforms.magento.fields.consumerKey.label'),
                  placeholder: getApiText('platforms.magento.fields.consumerKey.placeholder'),
                  description: getApiText('platforms.magento.fields.consumerKey.description'),
                },
                consumerSecret: {
                  label: getApiText('platforms.magento.fields.consumerSecret.label'),
                  placeholder: getApiText('platforms.magento.fields.consumerSecret.placeholder'),
                  description: getApiText('platforms.magento.fields.consumerSecret.description'),
                },
                accessToken: {
                  label: getApiText('platforms.magento.fields.accessToken.label'),
                  placeholder: getApiText('platforms.magento.fields.accessToken.placeholder'),
                  description: getApiText('platforms.magento.fields.accessToken.description'),
                },
                accessTokenSecret: {
                  label: getApiText('platforms.magento.fields.accessTokenSecret.label'),
                  placeholder: getApiText('platforms.magento.fields.accessTokenSecret.placeholder'),
                  description: getApiText('platforms.magento.fields.accessTokenSecret.description'),
                },
              },
            },
            bigcommerce: {
              label: getApiText('platforms.bigcommerce.label'),
              description: getApiText('platforms.bigcommerce.description'),
              fields: {
                storeHash: {
                  label: getApiText('platforms.bigcommerce.fields.storeHash.label'),
                  placeholder: getApiText('platforms.bigcommerce.fields.storeHash.placeholder'),
                  description: getApiText('platforms.bigcommerce.fields.storeHash.description'),
                },
                clientId: {
                  label: getApiText('platforms.bigcommerce.fields.clientId.label'),
                  placeholder: getApiText('platforms.bigcommerce.fields.clientId.placeholder'),
                  description: getApiText('platforms.bigcommerce.fields.clientId.description'),
                },
                accessToken: {
                  label: getApiText('platforms.bigcommerce.fields.accessToken.label'),
                  placeholder: getApiText('platforms.bigcommerce.fields.accessToken.placeholder'),
                  description: getApiText('platforms.bigcommerce.fields.accessToken.description'),
                },
              },
            },
          },
          supportedFeatures: {
            products: getApiText('platforms.supportedFeatures.products'),
            variants: getApiText('platforms.supportedFeatures.variants'),
            images: getApiText('platforms.supportedFeatures.images'),
            categories: getApiText('platforms.supportedFeatures.categories'),
            inventory: getApiText('platforms.supportedFeatures.inventory'),
            pricing: getApiText('platforms.supportedFeatures.pricing'),
            seo: getApiText('platforms.supportedFeatures.seo'),
            metafields: getApiText('platforms.supportedFeatures.metafields'),
            options: getApiText('platforms.supportedFeatures.options'),
            attributes: getApiText('platforms.supportedFeatures.attributes'),
          },
        }),
      },
      products: {
        title: getApiText('products.title'),
        description: getApiText('products.description'),
        refresh: getApiText('products.refresh'),
        noPlatforms: {
          title: getApiText('products.noPlatforms.title'),
          description: getApiText('products.noPlatforms.description'),
          addPlatform: getApiText('products.noPlatforms.addPlatform'),
        },
        noActivePlatforms: {
          title: getApiText('products.noActivePlatforms.title'),
          description: getApiText('products.noActivePlatforms.description'),
        },
        noSyncs: getApiText('products.noSyncs'),
        syncSummary: getApiText('products.syncSummary'),
        startSyncing: getApiText('products.startSyncing'),
        sampleSync: getApiText('products.sampleSync'),
        syncAll: getApiText('products.syncAll'),
        viewActivity: getApiText('products.viewActivity'),
        recentSyncs: getApiText('products.recentSyncs'),
        actions: {
          startSyncing: getApiText('products.actions.startSyncing'),
          syncSample: getApiText('products.actions.syncSample'),
          syncingSample: getApiText('products.actions.syncingSample'),
          syncAll: getApiText('products.actions.syncAll'),
          syncingAll: getApiText('products.actions.syncingAll'),
        },
      },
      activity: {
        title: getApiText('activity.title'),
        description: getApiText('activity.description'),
        refresh: getApiText('activity.refresh'),
        noActivity: getApiText('activity.noActivity'),
        noSyncActivityYet: getApiText('activity.noSyncActivityYet'),
        recentSyncOperationsTitle: getApiText('activity.recentSyncOperationsTitle'),
        table: {
          product: getApiText('activity.table.product'),
          platform: getApiText('activity.table.platform'),
          operation: getApiText('activity.table.operation'),
          status: getApiText('activity.table.status'),
          timestamp: getApiText('activity.table.timestamp'),
          details: getApiText('activity.table.details'),
        },
        status: {
          success: getApiText('activity.status.success'),
          failed: getApiText('activity.status.failed'),
        },
        operations: {
          create: getApiText('activity.operations.create'),
          update: getApiText('activity.operations.update'),
          delete: getApiText('activity.operations.delete'),
        },
        pagination: {
          showing: getApiText('activity.pagination.showing'),
          to: getApiText('activity.pagination.to'),
          of: getApiText('activity.pagination.of'),
          results: getApiText('activity.pagination.results'),
          previous: getApiText('activity.pagination.previous'),
          next: getApiText('activity.pagination.next'),
        },
        showing: getApiText('activity.showing'),
        to: getApiText('activity.to'),
        of: getApiText('activity.of'),
        results: getApiText('activity.results'),
        previous: getApiText('activity.previous'),
        next: getApiText('activity.next'),
        page: getApiText('activity.page'),
      },
      analytics: {
        title: getApiText('analytics.title'),
        description: getApiText('analytics.description'),
        refresh: getApiText('analytics.refresh'),
        loadingData: getApiText('analytics.loadingData'),
        cards: {
          connectedPlatforms: getApiText('analytics.cards.connectedPlatforms'),
          activePlatforms: getApiText('analytics.cards.activePlatforms'),
          totalSyncs: getApiText('analytics.cards.totalSyncs'),
          successRate: getApiText('analytics.cards.successRate'),
          platformDistribution: getApiText('analytics.cards.platformDistribution'),
        },
        connectedPlatforms: getApiText('analytics.connectedPlatforms'),
        activePlatforms: getApiText('analytics.activePlatforms'),
        totalSyncs: getApiText('analytics.totalSyncs'),
        successRate: getApiText('analytics.successRate'),
        platformDistribution: getApiText('analytics.platformDistribution'),
        platformDistributionTitle: getApiText('analytics.platformDistributionTitle'),
        acrossPlatforms: getApiText('analytics.acrossPlatforms'),
        successfulSyncs: getApiText('analytics.successfulSyncs'),
        syncs: getApiText('analytics.syncs'),
        across: getApiText('analytics.across'),
        platforms: getApiText('analytics.platforms'),
        of: getApiText('analytics.of'),
        successful: getApiText('analytics.successful'),
        connected: getApiText('analytics.connected'),
      },
      modal: {
        platformSelection: {
          placeholder: getApiText('modal.platformSelection.placeholder'),
        },
        configuration: {
          title: getApiText('modal.configuration.title'),
          description: getApiText('modal.configuration.description'),
          platform: {
            label: getApiText('modal.configuration.platform.label'),
          },
          name: {
            label: getApiText('modal.configuration.name.label'),
            placeholder: getApiText('modal.configuration.name.placeholder'),
          },
          platformSelection: {
            title: getApiText('modal.platforms.configuration.title'),
            description: getApiText('modal.platforms.configuration.description'),
            selectPlatform: getApiText('modal.platforms.configuration.selectPlatform'),
            placeholder: getApiText('modal.platforms.configuration.platformPlaceholder'),
          },
          configName: {
            label: getApiText('modal.platforms.configuration.configName'),
            placeholder: getApiText('modal.platforms.configuration.configNamePlaceholder'),
            description: getApiText('modal.platforms.configuration.configNameDescription'),
          },
          settings: {
            title: getApiText('modal.configuration.settings.title'),
          },
          mapping: {
            title: getApiText('modal.configuration.mapping.title'),
          },
          scheduling: {
            title: getApiText('modal.configuration.scheduling.title'),
            description: getApiText('modal.configuration.scheduling.description'),
            enableLabel: getApiText('modal.configuration.scheduling.enableLabel'),
            intervalLabel: getApiText('modal.configuration.scheduling.intervalLabel'),
            intervalPlaceholder: getApiText('modal.configuration.scheduling.intervalPlaceholder'),
            intervals: {
              hourly: {
                label: getApiText('modal.configuration.scheduling.intervals.hourly.label'),
                description: getApiText(
                  'modal.configuration.scheduling.intervals.hourly.description'
                ),
              },
              daily: {
                label: getApiText('modal.configuration.scheduling.intervals.daily.label'),
                description: getApiText(
                  'modal.configuration.scheduling.intervals.daily.description'
                ),
              },
              weekly: {
                label: getApiText('modal.configuration.scheduling.intervals.weekly.label'),
                description: getApiText(
                  'modal.configuration.scheduling.intervals.weekly.description'
                ),
              },
              monthly: {
                label: getApiText('modal.configuration.scheduling.intervals.monthly.label'),
                description: getApiText(
                  'modal.configuration.scheduling.intervals.monthly.description'
                ),
              },
            },
            note: getApiText('modal.configuration.scheduling.note'),
          },
          tabs: {
            basic: getApiText('modal.configuration.tabs.basic'),
            advanced: getApiText('modal.configuration.tabs.advanced'),
          },
          cards: {
            platformSelection: getApiText('modal.configuration.cards.platformSelection'),
          },
          actions: {
            cancel: getApiText('modal.configuration.actions.cancel'),
            save: getApiText('modal.configuration.actions.save'),
            saving: getApiText('modal.configuration.actions.saving'),
          },
          shopify: {
            sections: {
              automaticMappings: {
                title: getApiText('modal.configuration.shopify.sections.automaticMappings.title'),
                description: getApiText(
                  'modal.configuration.shopify.sections.automaticMappings.description'
                ),
              },
              collections: {
                title: getApiText('modal.configuration.shopify.sections.collections.title'),
                description: getApiText(
                  'modal.configuration.shopify.sections.collections.description'
                ),
              },
              productInformation: {
                title: getApiText('modal.configuration.shopify.sections.productInformation.title'),
                description: getApiText(
                  'modal.configuration.shopify.sections.productInformation.description'
                ),
              },
              pricing: {
                title: getApiText('modal.configuration.shopify.sections.pricing.title'),
                description: getApiText('modal.configuration.shopify.sections.pricing.description'),
              },
              productVariants: {
                title: getApiText('modal.configuration.shopify.sections.productVariants.title'),
                description: getApiText(
                  'modal.configuration.shopify.sections.productVariants.description'
                ),
              },
              inventory: {
                title: getApiText('modal.configuration.shopify.sections.inventory.title'),
                description: getApiText(
                  'modal.configuration.shopify.sections.inventory.description'
                ),
              },
              seoMarketing: {
                title: getApiText('modal.configuration.shopify.sections.seoMarketing.title'),
                description: getApiText(
                  'modal.configuration.shopify.sections.seoMarketing.description'
                ),
              },
              publishing: {
                title: getApiText('modal.configuration.shopify.sections.publishing.title'),
                description: getApiText(
                  'modal.configuration.shopify.sections.publishing.description'
                ),
              },
              advanced: {
                title: getApiText('modal.configuration.shopify.sections.advanced.title'),
                description: getApiText(
                  'modal.configuration.shopify.sections.advanced.description'
                ),
              },
            },
            fields: {
              productDescription: {
                label: getApiText('modal.configuration.shopify.fields.productDescription.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.productDescription.description'
                ),
              },
              vendorBrand: {
                label: getApiText('modal.configuration.shopify.fields.vendorBrand.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.vendorBrand.description'
                ),
              },
              productType: {
                label: getApiText('modal.configuration.shopify.fields.productType.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.productType.description'
                ),
              },
              urlHandle: {
                label: getApiText('modal.configuration.shopify.fields.urlHandle.label'),
                description: getApiText('modal.configuration.shopify.fields.urlHandle.description'),
              },
              mainPrice: {
                label: getApiText('modal.configuration.shopify.fields.mainPrice.label'),
                description: getApiText('modal.configuration.shopify.fields.mainPrice.description'),
              },
              compareAtPrice: {
                label: getApiText('modal.configuration.shopify.fields.compareAtPrice.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.compareAtPrice.description'
                ),
              },
              option1: {
                label: getApiText('modal.configuration.shopify.fields.option1.label'),
                description: getApiText('modal.configuration.shopify.fields.option1.description'),
              },
              option2: {
                label: getApiText('modal.configuration.shopify.fields.option2.label'),
                description: getApiText('modal.configuration.shopify.fields.option2.description'),
              },
              option3: {
                label: getApiText('modal.configuration.shopify.fields.option3.label'),
                description: getApiText('modal.configuration.shopify.fields.option3.description'),
              },
              barcode: {
                label: getApiText('modal.configuration.shopify.fields.barcode.label'),
                description: getApiText('modal.configuration.shopify.fields.barcode.description'),
              },
              weight: {
                label: getApiText('modal.configuration.shopify.fields.weight.label'),
                description: getApiText('modal.configuration.shopify.fields.weight.description'),
              },
              weightUnit: {
                label: getApiText('modal.configuration.shopify.fields.weightUnit.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.weightUnit.description'
                ),
              },
              stockQuantity: {
                label: getApiText('modal.configuration.shopify.fields.stockQuantity.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.stockQuantity.description'
                ),
              },
              inventoryManagement: {
                label: getApiText('modal.configuration.shopify.fields.inventoryManagement.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.inventoryManagement.description'
                ),
              },
              inventoryPolicy: {
                label: getApiText('modal.configuration.shopify.fields.inventoryPolicy.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.inventoryPolicy.description'
                ),
              },
              requiresShipping: {
                label: getApiText('modal.configuration.shopify.fields.requiresShipping.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.requiresShipping.description'
                ),
              },
              seoTitle: {
                label: getApiText('modal.configuration.shopify.fields.seoTitle.label'),
                description: getApiText('modal.configuration.shopify.fields.seoTitle.description'),
              },
              seoDescription: {
                label: getApiText('modal.configuration.shopify.fields.seoDescription.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.seoDescription.description'
                ),
              },
              tags: {
                label: getApiText('modal.configuration.shopify.fields.tags.label'),
                description: getApiText('modal.configuration.shopify.fields.tags.description'),
              },
              productStatus: {
                label: getApiText('modal.configuration.shopify.fields.productStatus.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.productStatus.description'
                ),
              },
              publishedScope: {
                label: getApiText('modal.configuration.shopify.fields.publishedScope.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.publishedScope.description'
                ),
              },
              publishDate: {
                label: getApiText('modal.configuration.shopify.fields.publishDate.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.publishDate.description'
                ),
              },
              templateSuffix: {
                label: getApiText('modal.configuration.shopify.fields.templateSuffix.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.templateSuffix.description'
                ),
              },
              fulfillmentService: {
                label: getApiText('modal.configuration.shopify.fields.fulfillmentService.label'),
                description: getApiText(
                  'modal.configuration.shopify.fields.fulfillmentService.description'
                ),
              },
              taxable: {
                label: getApiText('modal.configuration.shopify.fields.taxable.label'),
                description: getApiText('modal.configuration.shopify.fields.taxable.description'),
              },
            },
          },
          magento: {
            sections: {
              automaticMappings: {
                title: getApiText('modal.configuration.magento.sections.automaticMappings.title'),
                description: getApiText(
                  'modal.configuration.magento.sections.automaticMappings.description'
                ),
              },
              pricing: {
                title: getApiText('modal.configuration.magento.sections.pricing.title'),
                description: getApiText('modal.configuration.magento.sections.pricing.description'),
              },
              productAttributes: {
                title: getApiText('modal.configuration.magento.sections.productAttributes.title'),
                description: getApiText(
                  'modal.configuration.magento.sections.productAttributes.description'
                ),
              },
              inventory: {
                title: getApiText('modal.configuration.magento.sections.inventory.title'),
                description: getApiText(
                  'modal.configuration.magento.sections.inventory.description'
                ),
              },
              seoVisibility: {
                title: getApiText('modal.configuration.magento.sections.seoVisibility.title'),
                description: getApiText(
                  'modal.configuration.magento.sections.seoVisibility.description'
                ),
              },
            },
            fields: {
              basePrice: {
                label: getApiText('modal.configuration.magento.fields.basePrice.label'),
                description: getApiText('modal.configuration.magento.fields.basePrice.description'),
              },
              specialPrice: {
                label: getApiText('modal.configuration.magento.fields.specialPrice.label'),
                description: getApiText(
                  'modal.configuration.magento.fields.specialPrice.description'
                ),
              },
              weight: {
                label: getApiText('modal.configuration.magento.fields.weight.label'),
                description: getApiText('modal.configuration.magento.fields.weight.description'),
              },
              manufacturer: {
                label: getApiText('modal.configuration.magento.fields.manufacturer.label'),
                description: getApiText(
                  'modal.configuration.magento.fields.manufacturer.description'
                ),
              },
              color: {
                label: getApiText('modal.configuration.magento.fields.color.label'),
                description: getApiText('modal.configuration.magento.fields.color.description'),
              },
              size: {
                label: getApiText('modal.configuration.magento.fields.size.label'),
                description: getApiText('modal.configuration.magento.fields.size.description'),
              },
              stockQuantity: {
                label: getApiText('modal.configuration.magento.fields.stockQuantity.label'),
                description: getApiText(
                  'modal.configuration.magento.fields.stockQuantity.description'
                ),
              },
              manageStock: {
                label: getApiText('modal.configuration.magento.fields.manageStock.label'),
                description: getApiText(
                  'modal.configuration.magento.fields.manageStock.description'
                ),
              },
              backorders: {
                label: getApiText('modal.configuration.magento.fields.backorders.label'),
                description: getApiText(
                  'modal.configuration.magento.fields.backorders.description'
                ),
              },
              metaTitle: {
                label: getApiText('modal.configuration.magento.fields.metaTitle.label'),
                description: getApiText('modal.configuration.magento.fields.metaTitle.description'),
              },
              metaDescription: {
                label: getApiText('modal.configuration.magento.fields.metaDescription.label'),
                description: getApiText(
                  'modal.configuration.magento.fields.metaDescription.description'
                ),
              },
              urlKey: {
                label: getApiText('modal.configuration.magento.fields.urlKey.label'),
                description: getApiText('modal.configuration.magento.fields.urlKey.description'),
              },
            },
          },
          bigcommerce: {
            sections: {
              automaticMappings: {
                title: getApiText(
                  'modal.configuration.bigcommerce.sections.automaticMappings.title'
                ),
                description: getApiText(
                  'modal.configuration.bigcommerce.sections.automaticMappings.description'
                ),
              },
              pricing: {
                title: getApiText('modal.configuration.bigcommerce.sections.pricing.title'),
                description: getApiText(
                  'modal.configuration.bigcommerce.sections.pricing.description'
                ),
              },
              productDetails: {
                title: getApiText('modal.configuration.bigcommerce.sections.productDetails.title'),
                description: getApiText(
                  'modal.configuration.bigcommerce.sections.productDetails.description'
                ),
              },
              inventoryShipping: {
                title: getApiText(
                  'modal.configuration.bigcommerce.sections.inventoryShipping.title'
                ),
                description: getApiText(
                  'modal.configuration.bigcommerce.sections.inventoryShipping.description'
                ),
              },
              seoMarketing: {
                title: getApiText('modal.configuration.bigcommerce.sections.seoMarketing.title'),
                description: getApiText(
                  'modal.configuration.bigcommerce.sections.seoMarketing.description'
                ),
              },
            },
            fields: {
              basePrice: {
                label: getApiText('modal.configuration.bigcommerce.fields.basePrice.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.basePrice.description'
                ),
              },
              salePrice: {
                label: getApiText('modal.configuration.bigcommerce.fields.salePrice.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.salePrice.description'
                ),
              },
              retailPrice: {
                label: getApiText('modal.configuration.bigcommerce.fields.retailPrice.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.retailPrice.description'
                ),
              },
              brand: {
                label: getApiText('modal.configuration.bigcommerce.fields.brand.label'),
                description: getApiText('modal.configuration.bigcommerce.fields.brand.description'),
              },
              condition: {
                label: getApiText('modal.configuration.bigcommerce.fields.condition.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.condition.description'
                ),
              },
              warranty: {
                label: getApiText('modal.configuration.bigcommerce.fields.warranty.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.warranty.description'
                ),
              },
              stockLevel: {
                label: getApiText('modal.configuration.bigcommerce.fields.stockLevel.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.stockLevel.description'
                ),
              },
              trackInventory: {
                label: getApiText('modal.configuration.bigcommerce.fields.trackInventory.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.trackInventory.description'
                ),
              },
              width: {
                label: getApiText('modal.configuration.bigcommerce.fields.width.label'),
                description: getApiText('modal.configuration.bigcommerce.fields.width.description'),
              },
              height: {
                label: getApiText('modal.configuration.bigcommerce.fields.height.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.height.description'
                ),
              },
              depth: {
                label: getApiText('modal.configuration.bigcommerce.fields.depth.label'),
                description: getApiText('modal.configuration.bigcommerce.fields.depth.description'),
              },
              metaKeywords: {
                label: getApiText('modal.configuration.bigcommerce.fields.metaKeywords.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.metaKeywords.description'
                ),
              },
              searchKeywords: {
                label: getApiText('modal.configuration.bigcommerce.fields.searchKeywords.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.searchKeywords.description'
                ),
              },
              availability: {
                label: getApiText('modal.configuration.bigcommerce.fields.availability.label'),
                description: getApiText(
                  'modal.configuration.bigcommerce.fields.availability.description'
                ),
              },
            },
          },
          baseMapping: {
            common: {
              automatic: getApiText('modal.configuration.baseMapping.common.automatic'),
              useDefault: getApiText('modal.configuration.baseMapping.common.useDefault'),
              noMapping: getApiText('modal.configuration.baseMapping.common.noMapping'),
              select: getApiText('modal.configuration.baseMapping.common.select'),
              yesTrue: getApiText('modal.configuration.baseMapping.common.yesTrue'),
              noFalse: getApiText('modal.configuration.baseMapping.common.noFalse'),
            },
            transformationHints: {
              selectedValueOnly: getApiText(
                'modal.configuration.baseMapping.transformationHints.selectedValueOnly'
              ),
              valueUnitSeparately: getApiText(
                'modal.configuration.baseMapping.transformationHints.valueUnitSeparately'
              ),
              valueUnitText: getApiText(
                'modal.configuration.baseMapping.transformationHints.valueUnitText'
              ),
              htmlPreserved: getApiText(
                'modal.configuration.baseMapping.transformationHints.htmlPreserved'
              ),
              plainText: getApiText(
                'modal.configuration.baseMapping.transformationHints.plainText'
              ),
              trueFalseText: getApiText(
                'modal.configuration.baseMapping.transformationHints.trueFalseText'
              ),
              urlText: getApiText('modal.configuration.baseMapping.transformationHints.urlText'),
              numberAsText: getApiText(
                'modal.configuration.baseMapping.transformationHints.numberAsText'
              ),
              formattedDate: getApiText(
                'modal.configuration.baseMapping.transformationHints.formattedDate'
              ),
            },
            placeholders: {
              selectMapping: getApiText(
                'modal.configuration.baseMapping.placeholders.selectMapping'
              ),
              noMappingFor: getApiText('modal.configuration.baseMapping.placeholders.noMappingFor'),
            },
          },
        },
      },
      messages: {
        sampleSyncSuccess: getApiText('messages.sampleSyncSuccess'),
        syncAllSuccess: getApiText('messages.syncAllSuccess'),
        syncAllError: getApiText('messages.syncAllError'),
        testConnectionSuccess: getApiText('messages.testConnectionSuccess'),
        testConnectionError: getApiText('messages.testConnectionError'),
        configCreated: getApiText('messages.configCreated'),
        configUpdated: getApiText('messages.configUpdated'),
        configDeleted: getApiText('messages.configDeleted'),
        configEnabled: getApiText('messages.configEnabled'),
        configDisabled: getApiText('messages.configDisabled'),
        autoSyncEnabled: getApiText('messages.autoSyncEnabled'),
        autoSyncDisabled: getApiText('messages.autoSyncDisabled'),
      },
      validation: {
        platformRequired: getApiText('validation.platformRequired'),
        configurationNameRequired: getApiText('validation.configurationNameRequired'),
        platformSettingsRequired: getApiText('validation.platformSettingsRequired'),
        nameMaxLength: getApiText('validation.nameMaxLength'),
      },
      confirmations: {
        deleteConfiguration: getApiText('confirmations.deleteConfiguration'),
        deletePlatform: getApiText('confirmations.deletePlatform'),
        deleteDescription: getApiText('confirmations.deleteDescription'),
        confirmDelete: getApiText('confirmations.confirmDelete'),
        cancelDelete: getApiText('confirmations.cancelDelete'),
      },
      toasts: {
        configurationCreated: getApiText('toasts.configurationCreated'),
        configurationUpdated: getApiText('toasts.configurationUpdated'),
        configurationDeleted: getApiText('toasts.configurationDeleted'),
        platformEnabled: getApiText('toasts.platformEnabled'),
        platformDisabled: getApiText('toasts.platformDisabled'),
        autoSyncEnabled: getApiText('toasts.autoSyncEnabled'),
        autoSyncDisabled: getApiText('toasts.autoSyncDisabled'),
        connectionTestSuccess: getApiText('toasts.connectionTestSuccess'),
        connectionTestFailed: getApiText('toasts.connectionTestFailed'),
        syncSampleSuccess: getApiText('toasts.syncSampleSuccess'),
        syncAllSuccess: getApiText('toasts.syncAllSuccess'),
        debugSuccess: getApiText('toasts.debugSuccess'),
        previewMappingSuccess: getApiText('toasts.previewMappingSuccess'),
        platformMustBeEnabled: getApiText('toasts.platformMustBeEnabled'),
        failedToCreateConfiguration: getApiText('toasts.failedToCreateConfiguration'),
        failedToUpdateConfiguration: getApiText('toasts.failedToUpdateConfiguration'),
        failedToDeleteConfiguration: getApiText('toasts.failedToDeleteConfiguration'),
        failedToUpdatePlatformStatus: getApiText('toasts.failedToUpdatePlatformStatus'),
        failedToUpdateAutoSync: getApiText('toasts.failedToUpdateAutoSync'),
        failedToStartBulkSync: getApiText('toasts.failedToStartBulkSync'),
        failedToSyncAll: getApiText('toasts.failedToSyncAll'),
        debugFailed: getApiText('toasts.debugFailed'),
        failedToPreviewMapping: getApiText('toasts.failedToPreviewMapping'),
      },
      loadingStates: {
        loadingPermissions: getApiText('loadingStates.loadingPermissions'),
        loadingConfigurations: getApiText('loadingStates.loadingConfigurations'),
        syncing: getApiText('loadingStates.syncing'),
        syncingAll: getApiText('loadingStates.syncingAll'),
        saving: getApiText('loadingStates.saving'),
        testing: getApiText('loadingStates.testing'),
        deleting: getApiText('loadingStates.deleting'),
        enabling: getApiText('loadingStates.enabling'),
        disabling: getApiText('loadingStates.disabling'),
      },
      systemMessages: {
        autoMappingDescription: getApiText('systemMessages.autoMappingDescription'),
        noDataAvailable: getApiText('systemMessages.noDataAvailable'),
        noResultsFound: getApiText('systemMessages.noResultsFound'),
        errorLoadingData: getApiText('systemMessages.errorLoadingData'),
      },
    }),
    [t]
  );

  const getAttributesLabels = useMemo(
    () => ({
      title: getAttributesText('title'),
      description: getAttributesText('description'),
      placeholderText: getAttributesText('placeholderText'),
      manageAttributesButton: getAttributesText('manageAttributesButton'),
      manageAttributeGroupsButton: getAttributesText('manageAttributeGroupsButton'),
      attributeDescription: getAttributesText('attributeDescription'),
      loadAttributesButton: getAttributesText('loadAttributesButton'),
      loadingLabel: getAttributesText('loadingLabel'),
      noAttributesText: getAttributesText('noAttributesText'),
    }),
    [t]
  );

  const getAttributeGroupsLabels = useMemo(
    () => ({
      title: getAttributeGroupsText('title'),
      description: getAttributeGroupsText('description'),
      placeholderText: getAttributeGroupsText('placeholderText'),
      manageAttributeGroupsButton: getAttributeGroupsText('manageAttributeGroupsButton'),
      attributeGroupDescription: getAttributeGroupsText('attributeGroupDescription'),
      loadAttributeGroupsButton: getAttributeGroupsText('loadAttributeGroupsButton'),
      loadingLabel: getAttributeGroupsText('loadingLabel'),
      noAttributeGroupsText: getAttributeGroupsText('noAttributeGroupsText'),
    }),
    [t]
  );

  const getFamiliesLabels = useMemo(
    () => ({
      title: getFamiliesText('title'),
      description: getFamiliesText('description'),
      table: {
        columns: [
          { key: 'code', label: getFamiliesText('table.columns.0.label') },
          { key: 'label', label: getFamiliesText('table.columns.1.label') },
          {
            key: 'attributeGroups',
            label: getFamiliesText('table.columns.2.label'),
          },
          { key: 'usage', label: getFamiliesText('table.columns.3.label') },
          { key: 'createdAt', label: getFamiliesText('table.columns.4.label') },
          {
            key: 'actions',
            label: getFamiliesText('table.columns.5.label'),
            align: 'right' as const,
          },
        ],
        emptyState: {
          noResults: getFamiliesText('table.emptyState.noResults'),
          noData: getFamiliesText('table.emptyState.noData'),
        },
        searchPlaceholder: getFamiliesText('table.searchPlaceholder'),
        paginationPrefix: getFamiliesText('table.paginationPrefix'),
        paginationOf: getFamiliesText('table.paginationOf'),
        pageText: getFamiliesText('table.pageText'),
      },
    }),
    [t]
  );

  const getErrorLabels = useMemo(
    () => ({
      profileUpdate: getErrorsText('profileUpdate'),
      passwordChange: getErrorsText('passwordChange'),
      preferencesUpdate: getErrorsText('preferencesUpdate'),
      organizationUpdate: getErrorsText('organizationUpdate'),
      loadOrganization: getErrorsText('loadOrganization'),
      apiKeyCreation: getErrorsText('apiKeyCreation'),
      apiKeyDeletion: getErrorsText('apiKeyDeletion'),
      apiUsageStats: getErrorsText('apiUsageStats'),
    }),
    [t]
  );

  const getToastLabels = useMemo(
    () => ({
      attributes: {
        loadError: getToastText('attributes.loadError'),
        createSuccess: getToastText('attributes.createSuccess'),
        updateSuccess: getToastText('attributes.updateSuccess'),
        deleteSuccess: getToastText('attributes.deleteSuccess'),
        createError: getToastText('attributes.createError'),
        updateError: getToastText('attributes.updateError'),
        deleteError: getToastText('attributes.deleteError'),
      },
      locales: {
        createSuccess: getToastText('locales.createSuccess'),
        updateSuccess: getToastText('locales.updateSuccess'),
        deleteSuccess: getToastText('locales.deleteSuccess'),
        createError: getToastText('locales.createError'),
        updateError: getToastText('locales.updateError'),
        deleteError: getToastText('locales.deleteError'),
      },
      channels: {
        createSuccess: getToastText('channels.createSuccess'),
        updateSuccess: getToastText('channels.updateSuccess'),
        deleteSuccess: getToastText('channels.deleteSuccess'),
        createError: getToastText('channels.createError'),
        updateError: getToastText('channels.updateError'),
        deleteError: getToastText('channels.deleteError'),
      },
      currencies: {
        createSuccess: getToastText('currencies.createSuccess'),
        updateSuccess: getToastText('currencies.updateSuccess'),
        deleteSuccess: getToastText('currencies.deleteSuccess'),
        createError: getToastText('currencies.createError'),
        updateError: getToastText('currencies.updateError'),
        deleteError: getToastText('currencies.deleteError'),
      },
      priceTypes: {
        createSuccess: getToastText('priceTypes.createSuccess'),
        updateSuccess: getToastText('priceTypes.updateSuccess'),
        deleteSuccess: getToastText('priceTypes.deleteSuccess'),
        createError: getToastText('priceTypes.createError'),
        updateError: getToastText('priceTypes.updateError'),
        deleteError: getToastText('priceTypes.deleteError'),
      },
      profile: {
        updateSuccess: getToastText('profile.updateSuccess'),
        updateError: getToastText('profile.updateError'),
      },
      security: {
        passwordChangeSuccess: getToastText('security.passwordChangeSuccess'),
        passwordChangeError: getToastText('security.passwordChangeError'),
      },
      preferences: {
        updateSuccess: getToastText('preferences.updateSuccess'),
        updateError: getToastText('preferences.updateError'),
      },
      organization: {
        updateSuccess: getToastText('organization.updateSuccess'),
        updateError: getToastText('organization.updateError'),
        loadError: getToastText('organization.loadError'),
      },
      api: {
        keyCreateSuccess: getToastText('api.keyCreateSuccess'),
        keyDeleteSuccess: getToastText('api.keyDeleteSuccess'),
        keyUpdateSuccess: getToastText('api.keyUpdateSuccess'),
        keyCreateError: getToastText('api.keyCreateError'),
        keyDeleteError: getToastText('api.keyDeleteError'),
        keyUpdateError: getToastText('api.keyUpdateError'),
        usageStatsError: getToastText('api.usageStatsError'),
      },
    }),
    [t]
  );

  return {
    // Direct translation function with type safety
    t: tSafe,

    // Category-specific helpers
    getSettingsText,
    getValidationText,
    getPlaceholdersText,
    getTabsText,
    getProfileText,
    getSecurityText,
    getPreferencesText,
    getOrganizationText,
    getApiText,
    getAttributesText,
    getAttributeGroupsText,
    getFamiliesText,
    getErrorsText,
    getToastText,
    getAddLocaleModalText,
    getAttributeValueRowText,

    // Memoized complex helpers to prevent re-renders
    mainPage: getMainPageText,
    validation: getValidationLabels,
    placeholders: getPlaceholderLabels,
    tabs: getTabLabels,
    profile: getProfileLabels,
    security: getSecurityLabels,
    preferences: getPreferencesLabels,
    organization: getOrganizationLabels,
    api: getApiLabels,
    attributes: getAttributesLabels,
    attributeGroups: getAttributeGroupsLabels,
    families: getFamiliesLabels,
    errors: getErrorLabels,
    toast: getToastLabels,
  };
}

export type SettingspageTranslations = ReturnType<typeof useSettingspageTranslations>;
